import { useState } from "react";
import { Form, Button, Card, Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faCopy, faGraduationCap, faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import useAxiosPrivate from "../../../Pages/hooks/useAxiosPrivate";
import { baseURL } from "../../../API/API";
import { toast } from "react-toastify";

const MAX_TEACHER_IMAGE_BYTES = 1800 * 1024;

async function prepareTeacherImage(file) {
  if (!file || file.size <= MAX_TEACHER_IMAGE_BYTES) {
    return file;
  }

  const imageUrl = URL.createObjectURL(file);

  try {
    const image = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = imageUrl;
    });

    const maxSide = 1200;
    const scale = Math.min(1, maxSide / Math.max(image.width, image.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(image.width * scale));
    canvas.height = Math.max(1, Math.round(image.height * scale));

    const ctx = canvas.getContext("2d");
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.82));
    if (!blob) {
      throw new Error("Image compression failed");
    }

    const safeName = file.name.replace(/\.[^.]+$/, "") || "teacher-image";
    return new File([blob], `${safeName}.jpg`, { type: "image/jpeg" });
  } finally {
    URL.revokeObjectURL(imageUrl);
  }
}

export default function AddTeacher() {
  const axiosPrivate = useAxiosPrivate();
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [vodafoneCash, setVodafoneCash] = useState("");
  const [instaPay, setInstaPay] = useState("");
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Success state for generated credentials
  const [credentials, setCredentials] = useState(null);

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const preparedFile = await prepareTeacherImage(file);
      setImage(preparedFile);
      setImagePreview(URL.createObjectURL(preparedFile));
      if (preparedFile.size < file.size) {
        toast.info("تم ضغط الصورة تلقائيًا لتسهيل رفعها");
      }
    } catch (error) {
      console.error("Error preparing teacher image:", error);
      setImage(null);
      setImagePreview(null);
      toast.error("تعذر تجهيز الصورة، جرّب صورة أخرى أو حجم أصغر");
      e.target.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !subject) {
      toast.error("الرجاء إدخال اسم المعلم ومادته الدراسية");
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("name", name);
      formData.append("subject", subject);
      formData.append("description", description);
      formData.append("vodafone_cash", vodafoneCash);
      formData.append("instapay", instaPay);
      if (image) {
        formData.append("image", image);
      }

      const res = await axiosPrivate.post(`${baseURL}/learning/insert-teacher`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (res.data?.credentials) {
        setCredentials(res.data.credentials);
        toast.success("تم إضافة المعلم وتوليد الحساب بنجاح");
      } else {
        toast.success("تم إضافة المعلم بنجاح");
        // Reset form
        setName("");
        setSubject("");
        setDescription("");
        setVodafoneCash("");
        setInstaPay("");
        setImage(null);
        setImagePreview(null);
      }
    } catch (err) {
      console.error("Error inserting teacher:", err);
      toast.error(err.response?.data?.message || "حدث خطأ أثناء إضافة المعلم");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("تم النسخ للحافظة");
  };

  const handleReset = () => {
    setName("");
    setSubject("");
    setDescription("");
    setVodafoneCash("");
    setInstaPay("");
    setImage(null);
    setImagePreview(null);
    setCredentials(null);
  };

  return (
    <div className="p-4" dir="rtl">
      <div className="d-flex align-items-center gap-3 mb-4">
        <div className="p-2 bg-primary text-white rounded" style={{ backgroundColor: "var(--brand-purple)" }}>
          <FontAwesomeIcon icon={faPlus} style={{ fontSize: "1.5rem" }} />
        </div>
        <div>
          <h2 style={{ fontWeight: 800, margin: 0 }}>إضافة معلم جديد</h2>
          <p className="text-muted m-0">توليد حساب دخول تلقائي وربطه بملف المعلم</p>
        </div>
      </div>

      <Row className="g-4">
        {/* نموذج الإضافة */}
        <Col xs={12} lg={credentials ? 6 : 8} className="mx-auto">
          <Card className="shadow-sm border-0" style={{ borderRadius: "16px", backgroundColor: "var(--bg-card)" }}>
            <Card.Body className="p-4">
              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: "bold", color: "var(--clr-text-primary)" }}>اسم المعلم بالكامل</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="مثال: أ / محمد التهامي"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    style={{ borderRadius: "10px", padding: "10px" }}
                    disabled={!!credentials}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: "bold", color: "var(--clr-text-primary)" }}>المادة الدراسية / التخصص</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="مثال: أستاذ اللغة العربية"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    required
                    style={{ borderRadius: "10px", padding: "10px" }}
                    disabled={!!credentials}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: "bold", color: "var(--clr-text-primary)" }}>نبذة تعريفية عن المدرس</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    placeholder="شرح مبسط يظهر للطلاب في الملف الشخصي للمدرس..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    style={{ borderRadius: "10px", padding: "10px" }}
                    disabled={!!credentials}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: "bold", color: "var(--clr-text-primary)" }}>رقم فودافون كاش المعلم (اختياري)</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="مثال: 01012345678"
                    value={vodafoneCash}
                    onChange={(e) => setVodafoneCash(e.target.value)}
                    style={{ borderRadius: "10px", padding: "10px" }}
                    disabled={!!credentials}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: "bold", color: "var(--clr-text-primary)" }}>عنوان انستا باي المعلم (اختياري)</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="مثال: username@instapay"
                    value={instaPay}
                    onChange={(e) => setInstaPay(e.target.value)}
                    style={{ borderRadius: "10px", padding: "10px" }}
                    disabled={!!credentials}
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label style={{ fontWeight: "bold", color: "var(--clr-text-primary)" }}>الصورة الشخصية للمعلم</Form.Label>
                  {!credentials && (
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      style={{ borderRadius: "10px", padding: "10px" }}
                    />
                  )}
                  {imagePreview && (
                    <div className="mt-3 text-center">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={{ width: "120px", height: "120px", borderRadius: "50%", objectFit: "cover", border: "3px solid var(--brand-purple)" }}
                      />
                    </div>
                  )}
                </Form.Group>

                {!credentials ? (
                  <Button
                    type="submit"
                    variant="primary"
                    className="w-100 py-2.5"
                    disabled={submitting}
                    style={{ 
                      backgroundColor: "var(--brand-purple)", 
                      border: "none", 
                      borderRadius: "10px",
                      fontWeight: "bold",
                      fontSize: "1.1rem"
                    }}
                  >
                    {submitting ? "جاري إضافة المعلم وتوليد الحساب..." : "حفظ الحساب وتوليد بيانات الدخول"}
                  </Button>
                ) : (
                  <Button
                    onClick={handleReset}
                    variant="success"
                    className="w-100 py-2.5"
                    style={{ 
                      backgroundColor: "#22c55e", 
                      border: "none", 
                      borderRadius: "10px",
                      fontWeight: "bold",
                      fontSize: "1.1rem"
                    }}
                  >
                    إضافة معلم آخر
                  </Button>
                )}
              </Form>
            </Card.Body>
          </Card>
        </Col>

        {/* عرض بيانات الحساب المولدة */}
        {credentials && (
          <Col xs={12} lg={6}>
            <Card className="shadow-sm border-0 bg-success-light" style={{ borderRadius: "16px", backgroundColor: "#f0fdf4", border: "1px dashed #22c55e !important" }}>
              <Card.Body className="p-4 text-center">
                <div className="text-success mb-3">
                  <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: "3rem" }} />
                </div>
                <h4 style={{ fontWeight: 800, color: "#14532d" }}>تم توليد الحساب بنجاح!</h4>
                <p style={{ color: "#166534" }} className="mb-4">
                  يرجى نسخ وحفظ بيانات الدخول الموضحة أدناه وإرسالها للمدرس ليتمكن من الدخول للمنصة:
                </p>

                <div className="p-3 bg-white rounded shadow-sm text-start" style={{ border: "1px solid #bbf7d0", direction: "ltr" }}>
                  <div className="mb-3">
                    <strong style={{ color: "#3f3f46", fontSize: "0.9rem" }}>البريد الإلكتروني للرمز:</strong>
                    <div className="d-flex justify-content-between align-items-center mt-1">
                      <code style={{ fontSize: "1rem", color: "#1e293b" }}>{credentials.email}</code>
                      <Button size="sm" variant="outline-secondary" onClick={() => handleCopy(credentials.email)}>
                        <FontAwesomeIcon icon={faCopy} />
                      </Button>
                    </div>
                  </div>
                  <div>
                    <strong style={{ color: "#3f3f46", fontSize: "0.9rem" }}>كلمة المرور الافتراضية:</strong>
                    <div className="d-flex justify-content-between align-items-center mt-1">
                      <code style={{ fontSize: "1rem", color: "#1e293b" }}>{credentials.password}</code>
                      <Button size="sm" variant="outline-secondary" onClick={() => handleCopy(credentials.password)}>
                        <FontAwesomeIcon icon={faCopy} />
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="mt-4 p-2 bg-warning-light text-warning-dark rounded" style={{ backgroundColor: "#fffbeb", color: "#92400e", fontSize: "0.85rem" }}>
                  تنبيه هام: لن تتمكن من استرجاع كلمة المرور هذه مرة أخرى بعد الانتقال من هذه الصفحة.
                </div>
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>
    </div>
  );
}
